/* ============================================================
   SAM-1073 — create flow state: Kind · Details · Look · Booking & price (mock 21 oct), then the
   service is created and the full editor opens on it. Picking a kind applies `applySubtypeDefaults`
   exactly as the old two-step picker did. Edit mode has no flow ('form').
   ============================================================ */
import { useState } from 'react';
import { applySubtypeDefaults } from '../../../hooks/useServiceEditor';
import type { Editor } from '../ServiceEditorPage';
import {
  SUBTYPE_BACKEND_MAP, subtypeLabel, newServiceNoun, type CreateServiceType,
} from './serviceCreateTaxonomy';
import { startsFree, setFree, WIZARD_STEPS, stepIndex, type WizardStep } from './wizardModel';

export type CreatePhase = WizardStep | 'form';

export function useCreateServiceFlow(editor: Editor, isCreate: boolean) {
  const [phase, setPhase] = useState<CreatePhase>(isCreate ? 'kind' : 'form');
  const [createType, setCreateType] = useState<CreateServiceType | null>(null);
  const [createSubtype, setCreateSubtype] = useState<string | null>(null);

  /** A kind card: presets the form for it (Free for tables) and goes on to Details. */
  const pickKind = (type: CreateServiceType, subtype: string) => {
    setCreateType(type);
    setCreateSubtype(subtype);
    editor.setFormData((prev) => {
      const next = applySubtypeDefaults(prev, type, subtype);
      return setFree(next, startsFree(subtype));
    });
    setPhase('details');
  };

  const go = (step: WizardStep) => { if (isCreate) setPhase(step); };
  const next = () => {
    if (phase === 'form') return;
    const i = stepIndex(phase);
    if (i < WIZARD_STEPS.length - 1) setPhase(WIZARD_STEPS[i + 1].key);
  };
  const back = () => {
    if (phase === 'form') return;
    const i = stepIndex(phase);
    if (i > 0) setPhase(WIZARD_STEPS[i - 1].key);
  };

  const mapping = createSubtype ? SUBTYPE_BACKEND_MAP[createSubtype] : undefined;
  const subtypeNote = mapping && !mapping.supported ? mapping.note : undefined;
  const pickedLabel = createType && createSubtype ? subtypeLabel(createType, createSubtype) : undefined;
  const pickedNoun = createType && createSubtype ? newServiceNoun(createType, createSubtype) : undefined;

  return { phase, createType, createSubtype, pickKind, go, next, back, subtypeNote, pickedLabel, pickedNoun };
}
